// src/pages/Profile.tsx
import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Typography, Grid, Box, Chip, Alert, CircularProgress,
  Button, Card, CardContent, Divider, List, ListItem, ListItemIcon, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody,
  TableCell, TableHead, TableRow, TableContainer, Pagination, LinearProgress,
  IconButton, Tooltip, TextField, Select, MenuItem, FormControl, InputLabel,
  RadioGroup, FormControlLabel, Radio, Badge
} from '@mui/material';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TelegramIcon from '@mui/icons-material/Telegram';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TimerIcon from '@mui/icons-material/Timer';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCardIcon from '@mui/icons-material/AddCard';
import StarIcon from '@mui/icons-material/Star';

interface User {
  id: string;
  username: string;
  email: string;
  created_at: string;
  last_visit: string;
  tg_id?: number | null;
  tg_username?: string | null;
}

interface ActiveAccountsInfo {
  current: number;
  limit: number;
  can_activate_more: boolean;
}

interface UserLimitsInfo {
  user_level: string;
  is_staff: boolean;
  active_accounts: ActiveAccountsInfo;
  features: Record<string, boolean>;
}

interface UserSubscriptionInfo {
  user_id: string;
  user_level: string;
  subscription_status: string;
  is_staff: boolean;
  is_trial: boolean;
  trial_ends_at: string | null;
  trial_days_left: number | null;
  current_period_start: string | null;
  current_period_end: string | null;
  is_recurring: boolean;
  auto_renewal: boolean;
  card_last4: string | null;
  card_type: string | null;
  active_accounts_limit: number;
  features: Record<string, boolean>;
}

interface Payment {
  id: string;
  subscription_id: string;
  user_id: string;
  amount: number;
  currency: string;
  status: string;
  payment_type: string;
  yookassa_payment_id: string;
  yookassa_payment_url: string | null;
  paid_at: string | null;
  created_at: string;
  payment_metadata: Record<string, any> | null;
}

interface PaymentHistoryResponse {
  payments: Payment[];
  total_count: number;
}

interface SubscriptionSettings {
  active_account_limits: Record<string, number>;
  feature_limits: Record<string, Record<string, boolean>>;
  monthly_prices: Record<string, number>;
  discount_3_month_percent: Record<string, number>;
  discount_6_month_percent: Record<string, number>;
  discount_12_month_percent: Record<string, number>;
}

const FEATURE_TRANSLATIONS: Record<string, string> = {
  parsing: 'Парсинг',
  leadgen: 'Лидогенератор',
  warmup: 'Прогрев аккаунтов',
  api_access: 'API доступ',
  reactors: 'Реакторы',
  autosubscriptions: 'Автоподписка на каналы',
  neurocommenting: 'Нейрокомментинг',
  neurocopywright: 'Нейрокопирайтинг',
  broadcast: 'Рассылки',
  inviter: 'Инвайтер',
  story_mentions: 'Отметки в сторис',
};

const SUBSCRIPTION_STATUS_TRANSLATIONS: Record<string, string> = {
  active: 'Активна',
  trial: 'Триал',
  cancelled: 'Отменена',
  expired: 'Истекла',
  payment_failed: 'Ошибка оплаты',
  unverified: 'Не подтверждена',
};

const PAYMENT_STATUS_TRANSLATIONS: Record<string, string> = {
  succeeded: 'Успешно',
  pending: 'В ожидании',
  failed: 'Ошибка',
  cancelled: 'Отменен',
  refunded: 'Возврат',
};

const PAYMENT_TYPE_TRANSLATIONS: Record<string, string> = {
  one_time: 'Разовый',
  recurring: 'Автосписание',
  upgrade: 'Апгрейд',
  attach_card: 'Привязка карты',
};

const PLAN_DESCRIPTIONS: Record<string, string> = {
  free: 'Базовый парсинг',
  base: 'Для начинающих',
  pro: 'Популярный выбор',
  enterprise: 'Максимум возможностей',
  unlimited: 'Безлимит',
};

const Profile: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<UserSubscriptionInfo | null>(null);
  const [limits, setLimits] = useState<UserLimitsInfo | null>(null);
  const [subscriptionSettings, setSubscriptionSettings] = useState<SubscriptionSettings | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [totalPayments, setTotalPayments] = useState(0);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [attachCardOpen, setAttachCardOpen] = useState(false);
  const [detachCardOpen, setDetachCardOpen] = useState(false);

  const [selectedPlan, setSelectedPlan] = useState<string>('pro');
  const [billingPeriod, setBillingPeriod] = useState<number>(1);
  const [isRecurring, setIsRecurring] = useState<boolean>(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [attachCardLoading, setAttachCardLoading] = useState(false);
  const [detachCardLoading, setDetachCardLoading] = useState(false);

  const [upgradeImmediate, setUpgradeImmediate] = useState<boolean>(true);

  const pageSize = 10;

  useEffect(() => {
    if (!auth?.user) {
      navigate('/login');
      return;
    }
    fetchProfile();
  }, [auth?.user, navigate]);

  useEffect(() => {
    if (user) {
      fetchPayments();
    }
  }, [paymentsPage, user]);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const userRes = await api.get('/users/me');
      setUser(userRes.data);

      const subRes = await api.get('/subscriptions/info');
      setSubscription(subRes.data);

      const limitsRes = await api.get('/subscriptions/limits');
      setLimits(limitsRes.data);

      const settingsRes = await api.get('/subscriptions/subscription_settings');
      setSubscriptionSettings(settingsRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Не удалось загрузить профиль');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const paymentsRes = await api.get<PaymentHistoryResponse>('/payments/history', {
        params: { limit: pageSize, offset: (paymentsPage - 1) * pageSize },
      });
      setPayments(paymentsRes.data.payments || []);
      setTotalPayments(paymentsRes.data.total_count || 0);
    } catch (err) {
      console.error('Ошибка загрузки платежей:', err);
    }
  };

  const handlePurchase = async () => {
    setPurchaseLoading(true);
    try {
      const res = await api.post('/payments/purchase', {
        target_level: selectedPlan,
        billing_period_months: billingPeriod,
        is_recurring: isRecurring,
      });

      if (res.data.confirmation_url) {
        window.location.href = res.data.confirmation_url;
      }
    } catch (err: any) {
      alert('Ошибка создания платежа: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const res = await api.post('/subscriptions/cancel', {
        cancel_immediately: false,
        reason: 'Отмена по требованию пользователя',
      });
      setCancelOpen(false);
      alert(res.data.message || 'Подписка отменена в конце периода');
      fetchProfile();
    } catch (err: any) {
      alert('Ошибка отмены: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    }
  };

  const handleUpgrade = async () => {
    try {
      const res = await api.post('/subscriptions/upgrade', {
        new_level: selectedPlan,
        apply_immediately: upgradeImmediate,
      });

      if (res.data.confirmation_url) {
        window.location.href = res.data.confirmation_url;
      } else {
        alert(res.data.message || 'Тариф будет повышен');
        setUpgradeOpen(false);
        fetchProfile();
      }
    } catch (err: any) {
      alert('Ошибка повышения: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    }
  };

  const handleAttachCard = async () => {
    setAttachCardLoading(true);
    try {
      const res = await api.post('/subscriptions/attach-card', {});

      if (res.data.confirmation_url) {
        window.location.href = res.data.confirmation_url;
      }
    } catch (err: any) {
      alert('Ошибка привязки карты: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    } finally {
      setAttachCardLoading(false);
    }
  };

  const handleDetachCard = async () => {
    setDetachCardLoading(true);
    try {
      await api.post('/subscriptions/detach-card', {});
      setDetachCardOpen(false);
      alert('Карта отвязана, автопродление отключено');
      fetchProfile();
    } catch (err: any) {
      alert('Ошибка отвязки карты: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    } finally {
      setDetachCardLoading(false);
    }
  };

    const calculatePrice = (level: string, period: number): number => {
        if (!subscriptionSettings) return 0;
        const monthly = Number(subscriptionSettings.monthly_prices[level] || 0);
        if (monthly === 0) return 0;

        if (period === 1) return monthly;
        if (period === 3) return Math.round(Number(subscriptionSettings.discount_3_month_percent[level] || monthly * 3));
        if (period === 6) return Math.round(Number(subscriptionSettings.discount_6_month_percent[level] || monthly * 6));
        if (period === 12) return Math.round(Number(subscriptionSettings.discount_12_month_percent[level] || monthly * 12));

        return Math.round(monthly * period);
    };

    const calculateFullPrice = (level: string, period: number): number => {
        if (!subscriptionSettings) return 0;
        const monthly = Number(subscriptionSettings.monthly_prices[level] || 0);
        return Math.round(monthly * period);
    };

    const getDiscountPercent = (period: number): number => {
        if (period === 3) return 10;
        if (period === 6) return 15;
        if (period === 12) return 20;
        return 0;
    };

  const getLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'unlimited': return '#FFD700';
      case 'enterprise': return '#FF6B6B';
      case 'pro': return '#00BFFF';
      case 'base': return '#90EE90';
      case 'free': return '#B0BEC5';
      case 'trial': return '#03A9F4';
      case 'unverified': return '#757575';
      default: return '#B0BEC5';
    }
  };

  const getStatusColor = (status: string): "default" | "success" | "error" | "warning" | "info" => {
    switch (status.toLowerCase()) {
      case 'active': return 'success';
      case 'trial': return 'info';
      case 'cancelled': return 'warning';
      case 'expired': return 'error';
      case 'payment_failed': return 'error';
      case 'unverified': return 'default';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (status: string): "default" | "success" | "error" | "warning" | "info" => {
    switch (status.toLowerCase()) {
      case 'succeeded': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      case 'refunded': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" mt={3}>Загрузка профиля...</Typography>
      </Container>
    );
  }

  if (error || !user) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Не удалось загрузить профиль'}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/login')}>
          Войти заново
        </Button>
      </Container>
    );
  }

  const isUnverified = subscription?.user_level === 'unverified';
  const hasNoTelegram = !user.tg_id;

  const availablePlans = ['base', 'pro', 'enterprise', 'unlimited'];

  const totalPages = Math.ceil(totalPayments / pageSize);

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      {hasNoTelegram ? (
          <Alert severity="info" icon={<TelegramIcon/>} sx={{mb: 4, fontSize: '1.1rem'}}>
            <Typography variant="h6" gutterBottom>
              Активируйте бесплатный триал на 3 дня
            </Typography>
            <Typography>
              Для активации триала авторизуйтесь в нашем Telegram боте{' '}
              <strong>
                <a
                    href="https://t.me/tg_typhon_bot"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{color: 'inherit', textDecoration: 'underline'}}
                >
                  @tg_typhon_bot
                </a>
              </strong>
            </Typography>
          </Alert>

      ) : isUnverified ? (

          <Alert severity="warning" icon={<TelegramIcon/>} sx={{mb: 4, fontSize: '1.1rem'}}>
            <Typography variant="h6" gutterBottom>
              Авторизуйтесь через Telegram
            </Typography>
            <Typography>
              Чтобы активировать <strong>бесплатный триал на 3 дня</strong>, подтвердите
              ваш аккаунт Telegram через нашего бота.
            </Typography>
          </Alert>

      ) : subscription?.is_trial ? (

          <Alert severity="info" sx={{mb: 4}}>
            <Typography variant="h6">
              {subscription.trial_days_left && subscription.trial_days_left <= 1
                  ? '⏰ Триал заканчивается сегодня!'
                  : `🎉 Триал активен! Осталось ${subscription.trial_days_left} дней`}
            </Typography>

            <Typography variant="body2">
              После окончания триала вы перейдете на тариф FREE
              (2 аккаунта, только парсинг).
              Оформите подписку, чтобы сохранить доступ ко всем функциям.
            </Typography>
          </Alert>

      ) : null}

      <Grid container spacing={4}>
        {/* Левая колонка - профиль */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                {/*<Box*/}
                {/*  sx={{*/}
                {/*    width: 100,*/}
                {/*    height: 100,*/}
                {/*    mx: 'auto',*/}
                {/*    mb: 2,*/}
                {/*    borderRadius: '50%',*/}
                {/*    background: `linear-gradient(135deg, ${getLevelColor(subscription?.user_level || 'unverified')}, ${getLevelColor(subscription?.user_level || 'unverified')}88)`,*/}
                {/*    display: 'flex',*/}
                {/*    alignItems: 'center',*/}
                {/*    justifyContent: 'center',*/}
                {/*  }}*/}
                {/*>*/}
                {/*  <PersonIcon sx={{ fontSize: 60, color: 'white' }} />*/}
                {/*</Box>*/}
                <Typography variant="h5" fontWeight="bold" fontSize='1.9rem' gutterBottom>
                  {user.username}
                </Typography>
                <Chip
                  label={(subscription?.user_level || 'UNVERIFIED').toUpperCase()}
                  sx={{
                    bgcolor: getLevelColor(subscription?.user_level || 'unverified'),
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                  }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <List dense disablePadding>
                <ListItem>
                  <ListItemIcon><EmailIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Email" secondary={user.email} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><TelegramIcon color={user.tg_id ? 'success' : 'disabled'} /></ListItemIcon>
                  <ListItemText primary="Telegram" secondary={user.tg_username || 'Не подключен'} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CalendarTodayIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Регистрация" secondary={formatDate(user.created_at)} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CalendarTodayIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Последний вход" secondary={formatDateTime(user.last_visit)} />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Правая колонка */}
        <Grid size={{ xs: 12, md: 8 }}>
          {subscription && (
            <Card elevation={3} sx={{ mb: 3 }}>
              <CardContent>
                  <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                      <Typography variant="h6" fontWeight="bold">
                          Текущая подписка
                      </Typography>

                      {subscription.user_level !== 'free' && (
                          <Chip
                              label={
                                  SUBSCRIPTION_STATUS_TRANSLATIONS[subscription.subscription_status] ||
                                  subscription.subscription_status.toUpperCase()
                              }
                              color={getStatusColor(subscription.subscription_status)}
                              size="medium"
                          />
                      )}
                  </Box>

                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">Тариф</Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: getLevelColor(subscription.user_level) }}>
                      {subscription.user_level.toUpperCase()}
                    </Typography>
                    {subscription.is_staff && (
                      <Chip label="Сотрудник" size="small" color="secondary" sx={{ mt: 1 }} />
                    )}
                  </Grid>

                  {subscription.current_period_end &&
                      !isUnverified &&
                      subscription.user_level !== 'free' && (
                          <Grid size={{xs: 12, sm: 6}}>
                            <Typography variant="caption" color="text.secondary">
                              Активна до
                            </Typography>
                            <Typography variant="h6">
                              {formatDate(subscription.current_period_end)}
                            </Typography>

                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 1}}>
                              {subscription.is_recurring ? (
                                  <>
                                    <AutorenewIcon fontSize="small" color="success"/>
                                    <Typography variant="caption" color="success.main">
                                      Автопродление включено
                                    </Typography>
                                  </>
                              ) : (
                                  <>
                                    <WarningAmberIcon fontSize="small" color="warning"/>
                                    <Typography variant="caption" color="warning.main">
                                      Автопродление выключено
                                    </Typography>
                                  </>
                              )}
                            </Box>
                          </Grid>
                      )}

                  {subscription.card_last4 && (
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CreditCardIcon color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {subscription.card_type?.toUpperCase() || 'Карта'} •••• {subscription.card_last4}
                          </Typography>
                        </Box>
                        <Tooltip title="Отвязать карту">
                          <IconButton size="small" color="error" onClick={() => setDetachCardOpen(true)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {!isUnverified && subscription.user_level !== 'free' && (
                    <>
                      {subscription.subscription_status === 'active' && (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={() => setCancelOpen(true)}
                        >
                          Отменить подписку
                        </Button>
                      )}

                      {subscription.user_level !== 'unlimited' && subscription.user_level !== 'enterprise' && (
                        <Button
                          variant="contained"
                          startIcon={<UpgradeIcon />}
                          onClick={() => setUpgradeOpen(true)}
                        >
                          Повысить тариф
                        </Button>
                      )}

                      {!subscription.card_last4 && subscription.subscription_status === 'active' && (
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<AddCardIcon />}
                          onClick={() => setAttachCardOpen(true)}
                        >
                          Привязать карту
                        </Button>
                      )}
                    </>
                  )}

                  {(isUnverified || subscription.user_level === 'free' || subscription.is_trial) && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<ShoppingCartIcon />}
                      onClick={() => setPurchaseOpen(true)}
                      fullWidth
                    >
                      Купить подписку
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {limits && (
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Лимиты и доступные функции
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Активные аккаунты
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {limits.active_accounts.current} / {limits.active_accounts.limit}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(limits.active_accounts.current / limits.active_accounts.limit) * 100}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: limits.active_accounts.can_activate_more ? 'success.main' : 'error.main',
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {limits.active_accounts.can_activate_more
                      ? `Можно добавить еще ${limits.active_accounts.limit - limits.active_accounts.current}`
                      : 'Лимит достигнут'}
                  </Typography>
                </Box>

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 1 }}>
                  Доступные функции
                </Typography>
                <Grid container spacing={1}>
                  {Object.entries(limits.features).map(([feature, enabled]) => (
                    <Grid key={feature} size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {enabled ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <BlockIcon color="disabled" fontSize="small" />
                        )}
                        <Typography variant="body2" sx={{ color: enabled ? 'text.primary' : 'text.disabled' }}>
                          {FEATURE_TRANSLATIONS[feature] || feature}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* История платежей */}
        <Grid size={{ xs: 12 }}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                История платежей
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {payments.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <AccountBalanceWalletIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary">
                    Платежей пока нет
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Дата</TableCell>
                          <TableCell>Сумма</TableCell>
                          <TableCell>Статус</TableCell>
                          <TableCell>Тип</TableCell>
                          <TableCell>Тариф</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {payments.map(payment => (
                          <TableRow key={payment.id} hover>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDateTime(payment.created_at)}
                              </Typography>
                              {payment.paid_at && (
                                <Typography variant="caption" color="text.secondary">
                                  Оплачен: {formatDateTime(payment.paid_at)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1" fontWeight="bold">
                                {payment.amount.toLocaleString('ru-RU')} ₽
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={PAYMENT_STATUS_TRANSLATIONS[payment.status] || payment.status.toUpperCase()}
                                color={getPaymentStatusColor(payment.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {PAYMENT_TYPE_TRANSLATIONS[payment.payment_type] || payment.payment_type}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {payment.payment_metadata?.target_level?.toUpperCase() || '—'}
                              </Typography>
                              {payment.payment_metadata?.billing_period_months && (
                                <Typography variant="caption" color="text.secondary">
                                  {payment.payment_metadata.billing_period_months} мес.
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                      <Pagination
                        count={totalPages}
                        page={paymentsPage}
                        onChange={(_, page) => setPaymentsPage(page)}
                        color="primary"
                      />
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Модалка покупки подписки */}
      <Dialog open={purchaseOpen} onClose={() => setPurchaseOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #E5E7EB', pb: 2 }}>
          <Typography variant="h4" fontWeight="bold" textAlign="center">
            Выберите тариф
          </Typography>
          <Typography variant="body2" textAlign="center" color="text.secondary" mt={1}>
            Все тарифы включают 3-дневный бесплатный триал
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ py: 4 }}>
          {/* Выбор периода оплаты */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <RadioGroup
              row
              value={billingPeriod}
              onChange={(e) => setBillingPeriod(Number(e.target.value))}
              sx={{ justifyContent: 'center', gap: 3 }}
            >
              <FormControlLabel value={1} control={<Radio />} label="1 месяц" />
              <FormControlLabel
                value={3}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    3 месяца
                    <Chip label="-10%" size="small" color="success" />
                  </Box>
                }
              />
              <FormControlLabel
                value={6}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    6 месяцев
                    <Chip label="-15%" size="small" color="success" />
                  </Box>
                }
              />
              <FormControlLabel
                value={12}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    12 месяцев
                    <Chip label="-20%" size="small" color="success" />
                  </Box>
                }
              />
            </RadioGroup>
          </Box>

          {/* Карточки тарифов */}
        <Grid container spacing={3}>
  {subscriptionSettings &&
    availablePlans.map((planLevel) => {
      const isPopular = planLevel === 'pro';
      const accountLimit = subscriptionSettings.active_account_limits[planLevel] || 0;
      const features = subscriptionSettings.feature_limits[planLevel] || {};
      const monthlyPrice = Number(subscriptionSettings.monthly_prices[planLevel] || 0);
      const price = calculatePrice(planLevel, billingPeriod);
      const fullPrice = calculateFullPrice(planLevel, billingPeriod);
      const discountPercent = getDiscountPercent(billingPeriod);

      return (
        <Grid key={planLevel} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <Box
            sx={{
              position: 'relative',
              height: '100%',
              '&:hover .plan-card': {
                transform: 'translateY(-8px)',
                boxShadow: 12,
              },
            }}
          >
            {isPopular && (
              <Chip
                icon={<StarIcon />}
                label="ПОПУЛЯРНЫЙ"
                color="primary"
                sx={{
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 2,
                }}
              />
            )}

            <Card
              className="plan-card"
              onClick={() => setSelectedPlan(planLevel)}
              sx={{
                height: '100%',
                cursor: 'pointer',
                border:
                  selectedPlan === planLevel
                    ? '3px solid #1E40AF'
                    : '1px solid #E5E7EB',
                borderRadius: 3,
                position: 'relative',
                overflow: 'visible',
                transition: 'all 0.3s',
                boxShadow: selectedPlan === planLevel ? 10 : 2,
              }}
            >
              <CardContent>
                <Typography variant="h5" fontWeight="bold" textAlign="center">
                  {planLevel.toUpperCase()}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                  display="block"
                  mt={1}
                >
                  {PLAN_DESCRIPTIONS[planLevel]}
                </Typography>

                <Box textAlign="center" my={3}>
                   {billingPeriod > 1 && (
                    <Typography
                      variant="body1"
                      color="text.disabled"
                      sx={{ textDecoration: 'line-through', mb: 0.5 }}
                    >
                      {fullPrice.toLocaleString('ru-RU')} ₽
                    </Typography>
                  )}
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {price.toLocaleString('ru-RU')} ₽
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    {monthlyPrice.toLocaleString('ru-RU')} ₽ / мес
                  </Typography>

                  {discountPercent > 0 && (
                    <Typography
                      variant="caption"
                      color="success.main"
                      display="block"
                    >
                      Экономия {discountPercent}%
                    </Typography>
                  )}
                </Box>

                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  До {accountLimit} аккаунтов
                </Typography>

                <List dense>
                  {Object.entries(features).map(([key, enabled]) =>
                    enabled ? (
                      <ListItem key={key} disablePadding sx={{ py: 0.5 }}>
                        <CheckCircleIcon
                          fontSize="small"
                          color="success"
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="body2">
                          {FEATURE_TRANSLATIONS[key] || key}
                        </Typography>
                      </ListItem>
                    ) : null
                  )}
                </List>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      );
    })}
</Grid>

          {/* Автопродление */}
          <Box sx={{ mt: 5, p: 3, bgcolor: '#F9FAFB', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
              Способ оплаты
            </Typography>
            <RadioGroup
              value={isRecurring ? 'recurring' : 'one_time'}
              onChange={(e) => setIsRecurring(e.target.value === 'recurring')}
            >
              <FormControlLabel
                value="one_time"
                control={<Radio />}
                label={
                  <Box>
                    <Typography>Разовый платёж</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Без автопродления
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="recurring"
                control={<Radio />}
                label={
                  <Box>
                    <Typography>Подписка с автопродлением</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Карта сохраняется для автопродления
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
          <Button onClick={() => setPurchaseOpen(false)} size="large">
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handlePurchase}
            disabled={purchaseLoading}
            startIcon={purchaseLoading ? <CircularProgress size={20} /> : <ShoppingCartIcon />}
            size="large"
          >
            {purchaseLoading ? 'Создание платежа...' : `Оплатить ${calculatePrice(selectedPlan, billingPeriod).toLocaleString('ru-RU')} ₽`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Модалка отмены подписки - только в конце периода */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Отменить подписку?</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Вы уверены, что хотите отменить подписку?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            После отмены доступ к премиум-функциям сохранится до конца оплаченного периода, затем вы перейдёте на тариф FREE.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Нет</Button>
          <Button color="error" variant="contained" onClick={handleCancelSubscription}>
            Отменить в конце периода
          </Button>
        </DialogActions>
      </Dialog>

      {/* Модалка повышения тарифа */}
      <Dialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #E5E7EB', pb: 2 }}>
          <Typography variant="h4" fontWeight="bold" textAlign="center">
            Повысить тариф
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ py: 4 }}>
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <RadioGroup
              row
              value={upgradeImmediate ? 'immediate' : 'next_period'}
              onChange={(e) => setUpgradeImmediate(e.target.value === 'immediate')}
              sx={{ justifyContent: 'center', gap: 4 }}
            >
              <FormControlLabel
                value="immediate"
                control={<Radio />}
                label="Сейчас (с доплатой)"
              />
              <FormControlLabel
                value="next_period"
                control={<Radio />}
                label="С нового периода"
              />
            </RadioGroup>
          </Box>

          <Grid container spacing={3}>
            {subscriptionSettings && availablePlans
              .filter(p => subscription && p !== subscription.user_level)
              .map(planLevel => {
                const isPopular = planLevel === 'pro';
                const accountLimit = subscriptionSettings.active_account_limits[planLevel];
                const features = subscriptionSettings.feature_limits[planLevel];
                const monthlyPrice = Number(subscriptionSettings.monthly_prices[planLevel] || 0);

                return (
                  <Grid key={planLevel} size={{ xs: 12, sm: 6, md: 4 }} sx={{ position: 'relative' }}>
                    <Card
                      onClick={() => setSelectedPlan(planLevel)}
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'visible',
                        border: selectedPlan === planLevel ? '3px solid #1E40AF' : '1px solid #E5E7EB',
                        transition: 'all 0.3s',
                        '&:hover': { transform: 'translateY(-8px)', boxShadow: 12 },
                      }}
                    >
                      {isPopular && (
                        <Chip label="ПОПУЛЯРНЫЙ" color="primary" sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)' , zIndex: 1}}  />
                      )}
                      <CardContent>
                        <Typography variant="h5" fontWeight="bold" textAlign="center">
                          {planLevel.toUpperCase()}
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" color="primary" textAlign="center" mt={2}>
                          {monthlyPrice.toLocaleString('ru-RU')} ₽
                        </Typography>
                        <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                          в месяц
                        </Typography>

                        <Box mt={3}>
                          <Typography variant="subtitle2" color="text.secondary">
                            До {accountLimit} аккаунтов
                          </Typography>
                        </Box>

                        <List dense sx={{ mt: 2 }}>
                          {Object.entries(features || {}).map(([key, enabled]) =>
                            enabled && (
                              <ListItem key={key} disablePadding sx={{ py: 0.5 }}>
                                <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 1 }} />
                                <Typography variant="body2">{FEATURE_TRANSLATIONS[key] || key}</Typography>
                              </ListItem>
                            )
                          )}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
          <Button onClick={() => setUpgradeOpen(false)} size="large">
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handleUpgrade}
            startIcon={<UpgradeIcon />}
            size="large"
          >
            Повысить до {selectedPlan.toUpperCase()}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Привязка карты */}
      <Dialog open={attachCardOpen} onClose={() => setAttachCardOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddCardIcon />
            <Typography variant="h6">Привязать карту</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Привяжите карту для автопродления подписки
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachCardOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleAttachCard}
            disabled={attachCardLoading}
            startIcon={attachCardLoading ? <CircularProgress size={20} /> : <AddCardIcon />}
          >
            Привязать карту
          </Button>
        </DialogActions>
      </Dialog>

      {/* Отвязка карты */}
      <Dialog open={detachCardOpen} onClose={() => setDetachCardOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Отвязать карту?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Автопродление будет отключено
          </Alert>
          <Typography>
            Карта {subscription?.card_type?.toUpperCase() || ''} •••• {subscription?.card_last4} будет отвязана.
            Подписка останется активной до конца периода.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetachCardOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDetachCard}
            disabled={detachCardLoading}
            startIcon={detachCardLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            Отвязать
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile;